import {
  ALL_KINDS,
  COHORT_LABELS,
  DEFAULT_COHORTS,
  EVENT_GROUPS,
  KIND_LABELS,
  type EventGroupKey,
  type RosterActivityCohort,
  type RosterActivityEvent,
  type RosterActivityKind
} from "@/features/roster-activity";

export type PeriodGroupCounts = Record<EventGroupKey, number>;

export type NamedCount = {
  name: string;
  value: number;
};

export type CohortCount = NamedCount & {
  cohort: RosterActivityCohort;
};

export type PeriodSummary = {
  total: number;
  byGroup: PeriodGroupCounts;
  byCohort: CohortCount[];
  byKind: NamedCount[];
};

function emptyGroupCounts(): PeriodGroupCounts {
  return {
    changes: 0,
    retirements: 0,
    inMemoriam: 0
  };
}

export function summarizePeriod(events: RosterActivityEvent[]): PeriodSummary {
  const byGroup = emptyGroupCounts();
  const cohortCounts: Record<RosterActivityCohort, number> = {
    MEMBER: 0,
    ASSOCIATE: 0,
    STUDENT: 0
  };
  const kindCounts = Object.fromEntries(ALL_KINDS.map((kind) => [kind, 0])) as Record<RosterActivityKind, number>;

  for (const event of events) {
    const group = EVENT_GROUPS.find((entry) => entry.kinds.includes(event.kind));
    if (group) {
      byGroup[group.key] += 1;
    }
    cohortCounts[event.cohort] += 1;
    kindCounts[event.kind] += 1;
  }

  const byCohort = DEFAULT_COHORTS.map((cohort) => ({
    cohort,
    name: COHORT_LABELS[cohort],
    value: cohortCounts[cohort]
  })).filter((entry) => entry.value > 0);

  const byKind = (Object.keys(KIND_LABELS) as RosterActivityKind[])
    .map((kind) => ({
      name: KIND_LABELS[kind],
      value: kindCounts[kind] ?? 0
    }))
    .filter((entry) => entry.value > 0)
    .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));

  return {
    total: events.length,
    byGroup,
    byCohort,
    byKind
  };
}
